import React, { Component } from 'react';
import { TICKET_PAYMENT_METHOD } from '../utils/enums';
import { membershipStatus } from '../utils/membershipsApi';
import { formatYMD } from '../utils/formats';

var config = process.env;

// ENI-226: qué tipo de entrada viene elegido al abrir el modal. En la práctica casi todos los
// eventos tienen un único tipo llamado "General", y obligar al mostrador a elegirlo cada vez es
// un click de más con gente esperando.
// Sólo se consideran tipos VENDIBLES (active): el backend rechaza los desactivados con
// TYPE_UNAVAILABLE, así que preseleccionar uno inactivo cambiaría un click por un alert.
// Se compara sin acentos/mayúsculas/espacios porque el nombre lo escribe a mano un admin.
function defaultTicketTypeId(ticketTypes) {
    const vendibles = (ticketTypes || []).filter((tt) => tt && tt.active !== false && Number(tt.active) !== 0);
    if (vendibles.length === 0) {
        return '';
    }
    // ̀-ͯ = marcas diacríticas combinantes; con NFD delante, "Genéral" -> "general".
    const normalizar = (v) => String(v || '').trim().toLowerCase()
        .normalize('NFD').replace(/[\u0300-\u036f]/g, '');
    const porNombre = vendibles.find((tt) => normalizar(tt.name) === 'general');
    // `kind` es el enum del modelo y su default al crear un tipo, así que sirve de segunda
    // señal cuando el nombre está escrito distinto ("General A", "Entrada general"…).
    const porKind = vendibles.find((tt) => normalizar(tt.kind) === 'general');
    return String((porNombre || porKind || vendibles[0]).id);
}

// El tope de entradas de una sola venta (MAX_QTY_PER_PURCHASE del backend).
const MAX_POR_VENTA = 50;

// Lo que le falta vender a un evento: el cupo menos lo vendido, como mucho el tope de una venta.
// Sin cupo, o con el cupo ya cubierto, 1.
export function pendientesDeVender(capacity, sold) {
    if (capacity == null || String(capacity).trim() === '') return 1;
    const faltan = Math.floor(Number(capacity) - (Number(sold) || 0));
    if (!Number.isFinite(faltan) || faltan < 1) return 1;
    return Math.min(faltan, MAX_POR_VENTA);
}

// Venta presencial de entradas. Modal de Bootstrap (se abre con data-target="#modal-sell-event").
// Métodos de pago: Efectivo / Transferencia / POS / Mercado Pago (espejo de PRESENCIAL_METHODS del
// backend). Una entrada gratis (evento is_free o ticket $0) no pide método de pago (ENI-156).
// El cobro puede diferirse ("la paga después"): la entrada se emite impaga y se cobra desde la
// grilla de asistentes cuando la persona llega.
class ModalSellEventComponent extends Component {
    constructor(props) {
        super(props);
        this.state = {
            // Semilla defensiva: con el flujo actual esto es '' porque el modal monta ANTES de
            // que llegue el evento (la página lo monta apenas tiene el id de la URL). El default
            // de verdad lo pone componentDidUpdate cuando aterrizan los tipos.
            ticketTypeId: defaultTicketTypeId(props.ticketTypes),
            quantity: 1,
            paymentMethod: 'efectivo',
            deferPayment: false, // por defecto se cobra en el acto
            cedula: '',
            // ENI-214: datos del cliente. skipCustomer es el escape explícito del mostrador.
            customerName: '', customerLastName: '', customerEmail: '', customerPhone: '',
            skipCustomer: false,
            clientHint: null, // resultado del lookup por cédula
            // De qué cédula son los datos que hay en los campos: si el operador cambia la cédula,
            // los del cliente anterior NO pueden quedar (la entrada saldría a su nombre y el mail
            // iría a otra persona).
            datosCedula: '',
            socioHint: null,  // ¿la cédula es socia? (entrada de socio)
            disabled: false,
            ...this.arranqueSoloMostrador(),
        };
    }

    // Un evento sólo mostrador (`counterOnly`: el que nace del alta de evento privado) se vende
    // de una, a los invitados: si la venta del alta falló, la pantalla manda acá a terminarla.
    // Por eso el diálogo arranca con lo que falta vender y con «Cliente sin datos» tildado. Para
    // cualquier otro evento no cambia nada: 1 y destildado, como siempre.
    arranqueSoloMostrador = () => (this.props.counterOnly
        ? { quantity: pendientesDeVender(this.props.capacity, this.props.sold), skipCustomer: true }
        : {})

    // La página lo llama al apretar «Venta presencial», antes de que se vea el diálogo: cada
    // apertura de un evento sólo mostrador parte de lo que falta vender AHORA (después de una
    // venta el formulario vuelve a cero y el detalle del evento se relee). No cuelga de
    // `show.bs.modal`: la página carga dos jQuery y el evento puede no llegar.
    alAbrir = () => {
        if (this.props.counterOnly) this.setState(this.formularioVacio());
    }

    // «Queda impaga» no admite «Cliente sin datos» (sin datos no hay a quién cobrarle después):
    // elegirla destilda la casilla y aparecen los cinco datos. Antes la casilla quedaba tildada y
    // deshabilitada, con los datos escondidos, y el único camino era volver a «Cobrar ahora».
    onChangeCobro = (e) => {
        const later = e.target.value === 'later';
        this.setState({ deferPayment: later, ...(later ? { skipCustomer: false } : {}) });
    }

    // El default se aplica cuando CAMBIA la lista de tipos, no en cada render. Si el disparador
    // fuera "state.ticketTypeId === ''", al operador que elige a mano la opción vacía
    // "Seleccionar…" el select se le volvería a llenar solo en el próximo re-render (y este
    // componente re-renderiza con cada tecla del formulario).
    componentDidUpdate(prevProps) {
        const antes = prevProps.ticketTypes || [];
        const ahora = this.props.ticketTypes || [];
        if (ahora.length === 0) {
            return; // la página crea un [] nuevo en cada render mientras el evento no cargó
        }
        const mismaLista = antes.length === ahora.length
            && antes.every((tt, i) => tt && ahora[i] && tt.id === ahora[i].id);
        if (mismaLista) {
            return;
        }
        // Sólo se pisa si lo elegido dejó de ser válido (o no había nada elegido).
        const sigueValido = ahora.some((tt) => String(tt.id) === String(this.state.ticketTypeId));
        if (!sigueValido) {
            this.setState({ ticketTypeId: defaultTicketTypeId(ahora) });
        }
    }

    // El tipo de entrada decide DOS cosas del resto del formulario, así que cambiarlo no puede
    // dejar el estado anterior en pie: (a) el aviso de socio se calculó contra el tipo que
    // estaba elegido al salir del campo cédula —quedaba un "no es socio" en rojo sobre una
    // entrada general, o ningún aviso sobre una de socio—, y (b) "cliente sin datos" se ocultaba
    // en la UI para una entrada de socio pero seguía viajando en el submit, así que los datos
    // que el operador acababa de tipear se descartaban en silencio.
    handleChange = (e) => {
        const { name, value } = e.target;
        if (name !== 'ticketTypeId') {
            this.setState({ [name]: value });
            return;
        }
        const tt = (this.props.ticketTypes || []).find((t) => String(t.id) === String(value));
        const esSocio = !!tt && tt.kind === 'member';
        this.setState(
            // La entrada de socio es individual: una por socio (el backend lo exige igual).
            { ticketTypeId: value, socioHint: null, ...(esSocio ? { skipCustomer: false, quantity: 1 } : {}) },
            () => {
                // Recalcular el aviso contra el tipo nuevo si ya hay una cédula tipeada.
                const cedula = this.state.cedula.replace(/\D/g, '');
                if (cedula) this.lookupSocio(cedula);
            },
        );
    }

    // Si la cédula ya es de un cliente, traer sus datos en vez de hacerle tipear todo de nuevo.
    // Mismo molde que el walk-in de reservas y el POS de cafetería (onBlur + hint al lado).
    //
    // Se usa /clients y no /user porque "cliente" son DOS poblaciones: la ficha de facturación
    // (`user`, la de quien compró en el mostrador) y la cuenta de la tienda (`customer_account`,
    // la de quien se registró en la web). /clients/:cedula une las dos; /user/:cedula sólo ve la
    // primera, así que a un cliente puramente web lo daría por nuevo.
    // Entrada de socio: el backend la rechaza si la cédula no tiene membresía vigente a la fecha
    // del evento. Avisar acá, al salir del campo, evita cargar toda la venta para enterarse al
    // confirmar. Es un aviso: la decisión final es del backend.
    lookupSocio = (cedula) => {
        const selectedTt = (this.props.ticketTypes || []).find((tt) => String(tt.id) === String(this.state.ticketTypeId));
        if (!selectedTt || selectedTt.kind !== 'member') { this.setState({ socioHint: null }); return; }
        const fecha = String(this.props.eventStartsAt || '').slice(0, 10);
        // Vía membershipsApi y no fetch a mano: ahí vive el manejo del 1002, que distingue
        // "la sesión venció" de "tu rol no alcanza en esta sucursal". Con el fetch pelado, un
        // token vencido dejaba la pantalla sin aviso y la venta fallaba recién al confirmar.
        membershipStatus(cedula, fecha)
            .then((st) => {
                if (!st) { this.setState({ socioHint: null }); return; }
                this.setState({
                    socioHint: st.active
                        ? { ok: true, text: `Socio vigente${st.coveredUntil ? ` hasta el ${formatYMD(st.coveredUntil)}` : ''}.` }
                        : { ok: false, text: st.coveredUntil ? `No es socio a la fecha del evento (su membresía cubre hasta el ${formatYMD(st.coveredUntil)}).` : 'No es socio: la entrada de socio va a ser rechazada. Vendé una general o registrá la membresía primero.' },
                });
            })
            // El motivo se muestra en vez de tragarse: si el aviso queda mudo, el mostrador
            // no sabe si la persona no es socia o si la consulta no llegó, y lo descubre
            // recién cuando el backend rechaza la venta con el cliente enfrente.
            .catch((e) => this.setState({ socioHint: { ok: false, text: `No pudimos verificar la membresía: ${e.message}` } }));
    }

    lookupCliente = () => {
        // La cédula se guarda en dígitos pelados: si no se normaliza, un "4.711.223-3" tipeado
        // con puntos no matchea y el mostrador ve "Cliente nuevo" sobre alguien que ya existe.
        const cedula = this.state.cedula.replace(/\D/g, '');
        if (!cedula) { this.setState({ clientHint: null, socioHint: null }); return; }
        this.lookupSocio(cedula);
        const request = {
            method: 'GET',
            headers: { 'access-token': localStorage.getItem('token'), 'Content-Type': 'application/json' },
        };
        fetch(`${config.REACT_APP_BASE_URL}${config.REACT_APP_VERSION}clients/${encodeURIComponent(cedula)}`, request)
            .then((results) => results.json())
            .then((data) => {
                // El backend devuelve null cuando la cédula no tiene ni ficha ni cuenta.
                const cliente = (data && data.error === 0 && data.data) || null;
                // Cambió la cédula: lo que hay en los campos es de OTRA persona y se descarta.
                const otraPersona = !!this.state.datosCedula && this.state.datosCedula !== cedula;
                if (!cliente) {
                    this.setState({
                        clientHint: { ok: false, text: 'Cliente nuevo' },
                        datosCedula: cedula,
                        ...(otraPersona ? { customerName: '', customerLastName: '', customerEmail: '', customerPhone: '' } : {}),
                    });
                    return;
                }
                const perfil = cliente.profile || {};
                const cuenta = cliente.account || {};
                // Misma persona: sólo se completan los campos VACÍOS (lo tipeado es el dato más
                // fresco, misma regla que registerPresencialCustomer). Otra persona: se pisan.
                const completar = (actual, ...candidatos) =>
                    (actual.trim() && !otraPersona ? actual : (candidatos.find((v) => v && String(v).trim()) || ''));
                const nombre = completar(this.state.customerName, perfil.firstName, cuenta.firstName);
                const apellido = completar(this.state.customerLastName, perfil.lastName, cuenta.lastName);
                this.setState({
                    customerName: nombre,
                    customerLastName: apellido,
                    customerEmail: completar(this.state.customerEmail, perfil.email, cuenta.email),
                    customerPhone: completar(this.state.customerPhone, perfil.phone, cuenta.phone),
                    datosCedula: cedula,
                    clientHint: { ok: true, text: `✓ ${`${nombre} ${apellido}`.trim() || 'Cliente encontrado'}` },
                });
            })
            .catch((error) => {
                // El lookup es una comodidad: si falla, la venta se puede cargar a mano igual.
                console.error('No se pudo buscar el cliente:', error);
                this.setState({ clientHint: null });
            });
    }

    // El formulario vuelve a cero cada vez que el modal se cierra, se haya vendido o no: una venta
    // rechazada (un no socio, por ejemplo) dejaba nombre, mail y celular cargados, y la venta
    // siguiente salía a nombre de esa persona.
    formularioVacio = () => ({
        ticketTypeId: defaultTicketTypeId(this.props.ticketTypes), quantity: 1, paymentMethod: 'efectivo', deferPayment: false,
        cedula: '', customerName: '', customerLastName: '', customerEmail: '', customerPhone: '',
        skipCustomer: false, clientHint: null, socioHint: null, datosCedula: '', disabled: false,
        ...this.arranqueSoloMostrador(),
    })

    onModalHidden = () => this.setState(this.formularioVacio())

    componentDidMount() {
        if (window.$) window.$('#modal-sell-event').on('hidden.bs.modal', this.onModalHidden);
    }

    componentWillUnmount() {
        if (window.$) window.$('#modal-sell-event').off('hidden.bs.modal', this.onModalHidden);
    }

    closeModal = () => {
        const closeButton = document.getElementById('modal-sell-event-close');
        if (closeButton) {
            closeButton.click();
        }
    }

    // Vender aunque el cupo esté lleno. El cupo publicado es lo que se vende online, no un
    // techo del salón: a veces el local quiere meter más gente. El backend rechaza la primera
    // venta con SOLD_OUT; acá se pregunta y, si el operador confirma, se reintenta con
    // `overrideCapacity`. Sólo existe en esta venta presencial — la web nunca lo manda.
    onClickSell = () => {
        this.sell({ overrideCapacity: false });
    }

    sell = ({ overrideCapacity }) => {
        if (String(this.state.ticketTypeId).trim() === '') {
            alert('Elegí un tipo de entrada.');
            return;
        }
        const ttElegido = (this.props.ticketTypes || []).find((t) => String(t.id) === String(this.state.ticketTypeId));
        const quantity = ttElegido && ttElegido.kind === 'member' ? 1 : parseInt(this.state.quantity, 10);
        if (!(quantity > 0)) {
            alert('La cantidad debe ser mayor a 0.');
            return;
        }
        // El backend lo rechaza igual, pero avisar acá evita que el operador cargue toda la
        // venta para enterarse recién al confirmar.
        if (this.state.deferPayment && this.state.skipCustomer) {
            alert('Para dejar la entrada impaga hacen falta los datos del cliente: sin ellos no hay a quién cobrarle después.');
            return;
        }
        this.setState({ disabled: true });
        const body = {
            ticketTypeId: parseInt(this.state.ticketTypeId, 10),
            quantity,
            paymentMethod: this.state.paymentMethod, // efectivo|transferencia|pos|mercadopago (el back valida; ignora si es gratis)
            // Cobro diferido: la entrada se emite impaga y se cobra desde la grilla de asistentes.
            deferPayment: this.state.deferPayment,
        };
        if (overrideCapacity) {
            body.overrideCapacity = true;
        }
        // ENI-214: el cliente queda registrado y la entrada linkeada a su cuenta si tiene una
        // con la cédula verificada. Con "cliente sin datos" la venta va a consumidor final.
        const selectedTt = (this.props.ticketTypes || []).find((tt) => String(tt.id) === String(this.state.ticketTypeId));
        const isMemberTicket = !!selectedTt && selectedTt.kind === 'member';
        if (this.state.skipCustomer && !isMemberTicket) {
            body.skipCustomer = true;
        } else {
            body.customer = {
                cedula: this.state.cedula.trim(),
                name: this.state.customerName.trim(),
                lastName: this.state.customerLastName.trim(),
                email: this.state.customerEmail.trim(),
                phone: this.state.customerPhone.trim(),
            };
        }
        const request = {
            method: 'POST',
            headers: {
                'access-token': localStorage.getItem('token'),
                'Content-Type': 'application/json',
            },
            body: JSON.stringify(body),
        };
        fetch(`${config.REACT_APP_BASE_URL}${config.REACT_APP_VERSION}manage-events/${this.props.eventId}/sell`, request)
            .then((results) => results.json())
            .then((data) => {
                if (data.error > 0) {
                    // Cupo lleno: no es un error del operador, es una decisión suya. Se avisa
                    // con el cupo actual y, si confirma, se vuelve a mandar la MISMA venta con
                    // el flag. Si ya venía con el flag y volvió SOLD_OUT, cae al alert de abajo.
                    if (!overrideCapacity && data.data && data.data.error === 'SOLD_OUT') {
                        const detalle = data.data.message ? `${data.data.message}\n\n` : '';
                        this.setState({ disabled: false });
                        if (window.confirm(`${detalle}El cupo del evento ya está lleno. ¿Querés hacer la venta igual? Quedará más gente anotada que el cupo publicado.`)) {
                            this.sell({ overrideCapacity: true });
                        }
                        return;
                    }
                    // ENI-116: el mensaje de negocio (EVENT_PAST, SOLD_OUT, etc.) viene anidado en data.data.message
                    alert((data.data && data.data.message) || data.message);
                    this.setState({ disabled: false });
                    if (data.error == 1002) {
                        window.location.href = localStorage.getItem('homePageURL');
                    }
                } else {
                    // El reset vuelve al default, no a vacío: el modal no se desmonta y la
                    // página no refetchea el evento tras vender, así que las props no cambian y
                    // componentDidUpdate no se dispararía — sin esto, la preselección se perdía
                    // a partir de la SEGUNDA venta seguida, que es el caso típico del mostrador.
                    this.setState(this.formularioVacio());
                    this.closeModal();
                    if (typeof this.props.onSold === 'function') {
                        this.props.onSold();
                    }
                }
            })
            .catch((error) => {
                console.error('No se pudo registrar la venta:', error);
                alert('Error de conexión');
                this.setState({ disabled: false });
            });
    }

    render() {
        const ticketTypes = this.props.ticketTypes || [];
        // ENI-156: venta gratis -> no se pide método de pago. Espeja el backend
        // (sellPresencial: isFreeSale = event.is_free || ticketType.price === 0).
        const selectedTt = ticketTypes.find((tt) => String(tt.id) === String(this.state.ticketTypeId));
        const isFreeTicket = !!this.props.eventIsFree || (!!selectedTt && Number(selectedTt.price) === 0);
        // Entrada de socio: la cédula es la que prueba la membresía, así que "cliente sin datos"
        // no aplica (el backend la rechaza sin cédula).
        const isMemberTicket = !!selectedTt && selectedTt.kind === 'member';
        return (
            <div className="modal fade" id="modal-sell-event" role="dialog">
                <div className="modal-dialog">
                    <div className="modal-content">
                        <div className="modal-header">
                            <h4 className="modal-title">Venta presencial</h4>
                        </div>
                        <div className="modal-body">
                            <div className="form-group">
                                <label>Tipo de entrada</label>
                                <select name="ticketTypeId" className="form-control" value={this.state.ticketTypeId} onChange={this.handleChange}>
                                    <option value="">Seleccionar…</option>
                                    {ticketTypes.map((tt) => (
                                        <option key={tt.id} value={tt.id}>{tt.name} (${tt.price})</option>
                                    ))}
                                </select>
                                {isMemberTicket && (
                                    <small className="d-block text-muted">Entrada de socio: se valida con la cédula contra la membresía vigente a la fecha del evento.</small>
                                )}
                            </div>
                            <div className="form-group">
                                <label>Cantidad</label>
                                <input type="number" name="quantity" className="form-control" min="1" value={isMemberTicket ? 1 : this.state.quantity}
                                    disabled={isMemberTicket} onChange={this.handleChange} />
                                {isMemberTicket && (
                                    <small className="d-block text-muted">La entrada de socio es individual: una por socio. Para sus acompañantes, vendé entradas generales.</small>
                                )}
                            </div>
                            {/* Cobro: ahora o después. El diferido es la venta por teléfono —
                                alguien pide su lugar y lo paga al llegar. En una entrada gratis
                                no hay nada que cobrar, así que ni se ofrece. */}
                            {isFreeTicket ? (
                                <div className="form-group">
                                    <label>Método de pago</label>
                                    <p className="text-muted mb-0">Entrada gratuita — sin cobro.</p>
                                </div>
                            ) : (
                                <div className="form-group">
                                    <label>Cobro</label>
                                    <select name="cobro" className="form-control" value={this.state.deferPayment ? 'later' : 'now'}
                                        onChange={this.onChangeCobro}>
                                        <option value="now">Cobrar ahora</option>
                                        <option value="later">Queda impaga (se cobra después)</option>
                                    </select>
                                    {this.state.deferPayment ? (
                                        <small className="text-muted">
                                            La entrada se emite y toma su lugar, pero no se puede usar para entrar
                                            hasta que se cobre desde este mismo listado.
                                        </small>
                                    ) : (
                                        <select name="paymentMethod" className="form-control mt-2" value={this.state.paymentMethod} onChange={this.handleChange}>
                                            <option value="efectivo">{TICKET_PAYMENT_METHOD[1]}</option>
                                            <option value="transferencia">{TICKET_PAYMENT_METHOD[2]}</option>
                                            <option value="pos">{TICKET_PAYMENT_METHOD[5]}</option>
                                            <option value="mercadopago">{TICKET_PAYMENT_METHOD[3]}</option>
                                        </select>
                                    )}
                                </div>
                            )}
                            {/* ENI-214: los datos del cliente dejan de ser opcionales — la
                                entrada queda a nombre de alguien y linkeada a su cuenta. El
                                check de abajo es la salida para un invitado sin documento. */}
                            <div className="form-group">
                                <label className={(this.state.deferPayment || isMemberTicket) ? 'text-muted' : ''}>
                                    <input type="checkbox" name="skipCustomer" checked={this.state.skipCustomer && !isMemberTicket}
                                        disabled={this.state.deferPayment || isMemberTicket}
                                        onChange={(e) => this.setState({ skipCustomer: e.target.checked })} />
                                    {' '}Cliente sin datos (va a consumidor final)
                                </label>
                                {isMemberTicket && (
                                    <small className="d-block text-muted">No disponible en una entrada de socio: hace falta la cédula del socio.</small>
                                )}
                                {/* Una entrada impaga y anónima no se puede cobrar: no queda a quién
                                    reclamarle ni con qué encontrarla en el listado. */}
                                {this.state.deferPayment && (
                                    <small className="d-block text-muted">No disponible si la entrada queda impaga: hace falta saber a quién cobrarle.</small>
                                )}
                            </div>
                            {(!this.state.skipCustomer || isMemberTicket) && (
                                <div className="row">
                                    <div className="col-md-6 form-group">
                                        <label>Cédula *</label>
                                        <input type="text" name="cedula" className="form-control" value={this.state.cedula}
                                            onChange={(e) => this.setState({ cedula: e.target.value, clientHint: null, socioHint: null })}
                                            onBlur={this.lookupCliente} />
                                        {this.state.clientHint && (
                                            <small className={this.state.clientHint.ok ? 'text-success' : 'text-muted'}>{this.state.clientHint.text}</small>
                                        )}
                                        {this.state.socioHint && (
                                            <small className={`d-block ${this.state.socioHint.ok ? 'text-success' : 'text-danger'}`}>{this.state.socioHint.text}</small>
                                        )}
                                    </div>
                                    <div className="col-md-6 form-group">
                                        <label>Celular *</label>
                                        <input type="text" name="customerPhone" className="form-control" value={this.state.customerPhone} onChange={this.handleChange} />
                                    </div>
                                    <div className="col-md-6 form-group">
                                        <label>Nombre *</label>
                                        <input type="text" name="customerName" className="form-control" value={this.state.customerName} onChange={this.handleChange} />
                                    </div>
                                    <div className="col-md-6 form-group">
                                        <label>Apellido *</label>
                                        <input type="text" name="customerLastName" className="form-control" value={this.state.customerLastName} onChange={this.handleChange} />
                                    </div>
                                    <div className="col-12 form-group">
                                        <label>Email *</label>
                                        <input type="email" name="customerEmail" className="form-control" value={this.state.customerEmail} onChange={this.handleChange} />
                                    </div>
                                </div>
                            )}
                        </div>
                        <div className="modal-footer">
                            <button type="button" className="btn btn-accent" disabled={this.state.disabled} onClick={this.onClickSell}>
                                Confirmar venta
                            </button>
                            <button id="modal-sell-event-close" type="button" className="btn btn-outline-secondary" data-dismiss="modal">
                                Cerrar
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        );
    }
}

export default ModalSellEventComponent;
